/**
 * Survival of an in-page TRANSLATION (kitecorp/transferify-ui#2580).
 *
 * Chrome's "Translate this page" rewrites the document under React: every text node is replaced by
 * `<font><font>translated text</font></font>`, so the text node React's fiber still records as a
 * child of some element now lives one level deeper, under a wrapper React never created. The next
 * conditional re-render — pressing "Log in" and then "Email me a login code instead" is the
 * shortest route to one — asks the old parent to remove the old child and the browser answers
 * `NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child
 * of this node`. React has no recovery for that: the error reaches the router boundary and the
 * whole page becomes "This page could not be loaded". A translated visitor's app is simply dead.
 *
 * React declines to fix it in the library (facebook/react#11538, "Make React resilient to DOM
 * mutations from Google Translate" — closed without a general fix), and recommends exactly this
 * application-side workaround: make the two mutations React uses for conditional children total
 * rather than throwing, so the app stays alive.
 *
 * WHAT IS TRADED, stated plainly because absorbing is not repairing. The `<font>` wrapper holding
 * the translated copy is still a child of the original parent, so an absorbed `removeChild` leaves
 * that copy ON SCREEN: a translated page can keep showing a line React meant to delete — the login
 * modal keeps a mixture of both views' headings, which `e2e/translated-dom.spec.ts` walks through in
 * full. An absorbed `insertBefore` appends, so new content lands at the END of its container rather
 * than in position. Neither is repairable from here without re-implementing translation; both are
 * the accepted price of the app running at all, and a real translation session re-translates on
 * mutation, which repairs much of it.
 *
 * WHAT IS ALSO SWALLOWED, AND HOW IT IS NOW VISIBLE. This patches `Node.prototype`, so from here on ANY
 * wrong-parent `removeChild`/`insertBefore` — ours, React's, or a third-party library's — stops throwing.
 * That is not hypothetical: `utils/staleHydrationTree.ts` exists because a `<Seo>` unmount ran
 * `removeChild` with a null parent and crashed, and with this guard installed that crash would have been
 * absorbed in silence. So the guard COUNTS what it absorbs and reports the count once per session, with
 * the first absorbed call's own first frames, through `errors/clientErrorReport.ts` to the api's
 * client-error ingest (kitecorp/transferify-api-service#2644, kitecorp/transferify-ui#2581) — in
 * production, which is where the swallowing matters and where there is no console anyone reads. Weigh that
 * before reaching for `Node.prototype` again, but the absorbing is no longer silent.
 *
 * WHY THE COUNT IS REPORTED AT THE END AND NOT PER CALL. A real translation session re-translates on every
 * mutation, so absorptions arrive in bursts of tens; one report each would spend the endpoint's whole abuse
 * budget on one reader and leave nothing for anybody else's. One report carrying the total answers the
 * question that is actually asked — is this happening, and how much — and `pagehide` is when the total is
 * final. It is deliberately not `visibilitychange`: a reader switching tabs and coming back would report a
 * partial count and then nothing more, because the session gate has been spent.
 *
 * WHY NOT `<meta name="google" content="notranslate">` or `translate="no"`: it works by switching
 * translation OFF, for every visitor, on every page. We serve three brands across several
 * languages, and a reader who asked their browser for their own language would instead get an
 * untranslated page with no explanation. The owner decided the app must keep being translatable and
 * absorb the mutations instead.
 *
 * This is a deliberately NARROW patch of two `Node.prototype` methods, and it only diverges from
 * native behaviour in the case that natively throws. Plain module, no React: installed once from
 * the SSG entry's client-only callback, the same way `staleChunkRecovery` is.
 */
import {prepareCaughtErrorReporting, reportCaughtError} from "errors/clientErrorReport";
import {resolveActiveLocale} from "i18n/localePreference";
import {isDev} from "utils/ReactHelpers";

/** The one-time diagnostic. A constant so its single delivery can be asserted against this text. */
export const TRANSLATED_DOM_GUARD_WARNING = "A page translation moved DOM nodes React still owns; "
	+ "removeChild/insertBefore were absorbed instead of throwing (transferify-ui#2580).";

/**
 * Marks the patched methods. Kept ON THE FUNCTIONS rather than in a module variable so the
 * already-installed answer survives a second module instance and disappears the moment anything
 * restores the originals — a module flag would claim an install that is no longer there.
 */
const GUARD_MARKER = "__transferifyTranslatedDomGuard";

type GuardedMethod = {[GUARD_MARKER]?: true};

export type TranslatedDomGuardOptions = {
	/** Where the one-time diagnostic goes. Defaults to the console in development, nowhere in production. */
	warn?: (message: string) => void;
	/**
	 * Where the session's absorption COUNT goes, with the first absorbed call's own stack. Defaults to the
	 * first-party client-error report in production and to nothing in development, where the console
	 * warning above is the signal and a developer's own broken render would otherwise spend the budget.
	 *
	 * Injected rather than imported at the call site so the unit tests can assert the exact absorption
	 * record without a network stub — the report's own rules are tested in `errors/clientErrorReport.ts`.
	 */
	report?: (absorption: TranslatedDomAbsorption) => void;
	/**
	 * Called once, on the FIRST absorption, to warm whatever {@link report} will need. Defaults to loading
	 * the lazy chunk the first-party report resolves its brand through — see
	 * {@code prepareReportingInProduction} for why that cannot wait for `pagehide`.
	 */
	prepareReporting?: () => void;
};

/** What the guard absorbed over one session: how many calls, and where the first one came from. */
export type TranslatedDomAbsorption = {
	absorbedCount: number;
	/** The first absorbed call's own stack, so a report can name the code that was mutating the DOM. */
	firstAbsorbedStack: string;
};

function warnInDevelopment(message: string): void {
	if (isDev()) {
		console.warn(message);
	}
}

function reportInProduction(absorption: TranslatedDomAbsorption): void {
	if (isDev()) {
		return;
	}
	void reportCaughtError({
		surface: "TRANSLATED_DOM_GUARD",
		// A synthetic Error carrying the first absorbed call's stack: `clientErrorReport` takes the caught
		// value of any shape and knows how to reduce one to a name, a message and two frames, so the guard
		// does not need a second copy of that reduction.
		error: absorptionError(absorption),
		locale: resolveActiveLocale(),
		pathname: window.location.pathname,
		absorbedCount: absorption.absorbedCount,
	});
}

/**
 * Warms the reporting path on the first absorption, in production only.
 *
 * The report itself happens at `pagehide`, which is the last moment a page has; the brand resolver it
 * needs is in a lazy chunk, and a fetch started at `pagehide` would usually not finish. An absorption
 * happens long before that. In development there is nothing to warm, because the console warning is the
 * signal and nothing is posted.
 */
function prepareReportingInProduction(): void {
	if (!isDev()) {
		prepareCaughtErrorReporting();
	}
}

/**
 * How many frames at the top of an absorbed call's stack are the guard's own: `absorb`, then the patched
 * `removeChild` or `insertBefore` that called it.
 */
const GUARD_OWN_FRAMES = 2;

/**
 * A stack captured inside the guard, rewritten so its first FRAME line is the caller's.
 *
 * The report keeps two frame lines, and the guard's own two are the same in every report and say nothing
 * about who mutated the DOM, so they are dropped here. The message line goes first in the result whatever
 * the engine wrote, because `clientErrorReport` drops the first line: V8 starts a stack with it, while
 * Firefox and Safari start with the first frame.
 */
function callerStack(stack: string): string {
	const lines = stack.split("\n");
	const frames = lines[0]?.includes(TRANSLATED_DOM_GUARD_WARNING) ? lines.slice(1) : lines;
	return [TRANSLATED_DOM_GUARD_WARNING, ...frames.slice(GUARD_OWN_FRAMES)].join("\n");
}

/** The absorption as an `Error`, so one reduction rule covers it and every caught value. */
export function absorptionError(absorption: TranslatedDomAbsorption): Error {
	const error = new Error(TRANSLATED_DOM_GUARD_WARNING);
	error.name = ABSORPTION_ERROR_NAME;
	error.stack = absorption.firstAbsorbedStack;
	return error;
}

/**
 * The name the guard's report carries: an identifier, because the api admits only one in that field, and
 * one shaped as an error class (capitalised words ending in `Error`), because the api keeps a name only in
 * that shape and writes anything else as `[token]` (kitecorp/transferify-api-service#2649).
 */
export const ABSORPTION_ERROR_NAME = "TranslatedDomAbsorptionError";

/**
 * Arranges for the session's absorption total to be reported once, when the page goes away.
 *
 * `pagehide` rather than `unload` or `beforeunload`: those two make a page ineligible for the back/forward
 * cache in every current browser, so listening on them would cost every reader a slower back button to
 * measure a rare fault. `pagehide` fires for a real navigation AND when the page enters that cache, and
 * `fireBeacon`'s `keepalive` fetch is the one request shape that survives it.
 *
 * `{once: true}` is the gate: a page can be shown and hidden repeatedly, and the total is reported for the
 * first hide. The session gate inside `reportClientError` is the second line of defence, so even a second
 * listener could not double-report.
 */
function reportAbsorptionsOnce(
	read: () => TranslatedDomAbsorption,
	report: (absorption: TranslatedDomAbsorption) => void): void {
	if (typeof window === "undefined") {
		return;
	}
	window.addEventListener("pagehide", () => {
		const absorption = read();
		// Nothing absorbed is nothing to say. The api refuses a zero count, and a report that the guard was
		// installed and did nothing would be one request per session from every reader in the world.
		if (absorption.absorbedCount > 0) {
			report(absorption);
		}
	}, {once: true});
}

/**
 * Patches `Node.prototype.removeChild` / `insertBefore` so a translated document cannot crash a
 * re-render. Returns whether this call installed them: `false` means either no DOM (the SSG
 * prerender) or an install that is already in place.
 */
export function installTranslatedDomGuard(options: TranslatedDomGuardOptions = {}): boolean {
	// Read lazily, never at module scope: the SSG prerender evaluates this module in Node, where
	// there is no `Node` constructor and nothing to patch.
	if (typeof Node === "undefined") {
		return false;
	}
	const prototype = Node.prototype;
	const nativeRemoveChild: typeof prototype.removeChild & GuardedMethod = prototype.removeChild;
	const nativeInsertBefore: typeof prototype.insertBefore & GuardedMethod = prototype.insertBefore;
	// BOTH are checked. Reading only one would let an asymmetric restore — anything that puts the
	// native `removeChild` back without the native `insertBefore` — capture an already-guarded
	// `insertBefore` as its "native" and wrap it a second time, while the marker still reported an
	// install in place.
	if (nativeRemoveChild[GUARD_MARKER] === true || nativeInsertBefore[GUARD_MARKER] === true) {
		return false;
	}
	/**
	 * `parentNode` read through the prototype accessor rather than off the node.
	 *
	 * `HTMLFormElement` is `[LegacyOverrideBuiltIns]`: a control NAMED `parentNode` inside a form
	 * becomes an own property that shadows the getter, so `form.parentNode` would answer the input and
	 * the guard would refuse a removal that was perfectly legitimate. Nothing in this app names a
	 * control from user input today, but this patch governs every removal in the document, including a
	 * third-party library's. jsdom does not implement that shadowing, so there is no unit test for it —
	 * a test would pass with or without this line.
	 */
	const parentNodeOf = Object.getOwnPropertyDescriptor(prototype, "parentNode")?.get;
	const parentOf = (node: Node): ParentNode | Node | null =>
		parentNodeOf === undefined ? node.parentNode : parentNodeOf.call(node) as Node | null;
	const warn = options.warn ?? warnInDevelopment;
	const report = options.report ?? reportInProduction;
	let warned = false;
	let absorbedCount = 0;
	let firstAbsorbedStack = "";
	/**
	 * Counts one absorbed mutation, warns on the first, and keeps the FIRST call's stack.
	 *
	 * The first rather than the latest: a translation session absorbs the same handful of call sites over
	 * and over, so the first is as representative as any — and keeping the latest would mean the reported
	 * stack depended on when the reader happened to leave the page.
	 */
	const prepare = options.prepareReporting ?? prepareReportingInProduction;
	const absorb = (): void => {
		absorbedCount += 1;
		if (firstAbsorbedStack === "") {
			prepare();
			firstAbsorbedStack = callerStack(new Error(TRANSLATED_DOM_GUARD_WARNING).stack ?? "");
		}
		if (warned) {
			return;
		}
		warned = true;
		warn(TRANSLATED_DOM_GUARD_WARNING);
	};
	reportAbsorptionsOnce(() => ({absorbedCount, firstAbsorbedStack}), report);

	const guardedRemoveChild: typeof prototype.removeChild & GuardedMethod =
		function removeChild<T extends Node>(this: Node, child: T): T {
			if (parentOf(child) === this) {
				nativeRemoveChild.call(this, child);
			} else {
				absorb();
			}
			// Returned from both branches rather than from each call, because `Function.prototype.call`
			// erases the generic back to `Node` — and the answer is the same node either way: a removal
			// hands back what it removed, and the absorbed case hands back the node somebody else moved.
			return child;
		};
	const guardedInsertBefore: typeof prototype.insertBefore & GuardedMethod =
		function insertBefore<T extends Node>(this: Node, node: T, reference: Node | null): T {
			// A null reference already MEANS "append", and is not a translation artefact.
			if (reference !== null && parentOf(reference) !== this) {
				absorb();
				this.appendChild(node);
			} else {
				nativeInsertBefore.call(this, node, reference);
			}
			return node;
		};
	guardedRemoveChild[GUARD_MARKER] = true;
	guardedInsertBefore[GUARD_MARKER] = true;
	prototype.removeChild = guardedRemoveChild;
	prototype.insertBefore = guardedInsertBefore;
	return true;
}
